/**
 * Find-in-graph matcher (tender sheet #40).
 *
 * Pure function so the Workbench search box and its tests share one rule:
 * a case-insensitive substring match against
 *   - nodes: id, address, label, user_label, entity_name / entityName,
 *            entity.name, display_name, name
 *   - edges: id, tx_hash, and every tx_hash inside the edge's
 *            trace_transactions / all_transactions / transactions lists
 *            (consolidated edges carry their hashes there)
 *
 * Nodes come first in the result, then edges, each in input order, so the
 * "first match" the Workbench selects is a wallet whenever one matches.
 * The query is trimmed; an empty query matches nothing.
 */

export interface FindableElement {
    data?: Record<string, any> | null;
}

export interface GraphFindMatch {
    id: string;
    kind: 'node' | 'edge';
    /** Which field produced the match, for the status line and tests. */
    field: string;
}

const NODE_FIELDS = [
    'id',
    'address',
    'label',
    'user_label',
    'entity_name',
    'entityName',
    'display_name',
    'name',
] as const;

const EDGE_TX_LISTS = ['trace_transactions', 'all_transactions', 'transactions'] as const;

function hit(value: unknown, needle: string): boolean {
    return typeof value === 'string' && value.length > 0 && value.toLowerCase().includes(needle);
}

/** Return the field that matched on one element's data, or null. */
export function matchElementData(data: Record<string, any> | null | undefined, query: string): string | null {
    const needle = (query || '').trim().toLowerCase();
    if (!needle || !data) return null;
    const isEdge = data.source != null && data.target != null;
    if (isEdge) {
        if (hit(data.id, needle)) return 'id';
        if (hit(data.tx_hash, needle)) return 'tx_hash';
        for (const list of EDGE_TX_LISTS) {
            const txs = data[list];
            if (!Array.isArray(txs)) continue;
            for (const tx of txs) {
                if (tx && (hit(tx.tx_hash, needle) || hit(tx.hash, needle))) return `${list}.tx_hash`;
            }
        }
        return null;
    }
    for (const field of NODE_FIELDS) {
        if (hit(data[field], needle)) return field;
    }
    if (data.entity && typeof data.entity === 'object' && hit(data.entity.name, needle)) return 'entity.name';
    return null;
}

/** Match a query against canvas elements. Nodes first, then edges. */
export function findGraphMatches(elements: readonly FindableElement[], query: string): GraphFindMatch[] {
    const needle = (query || '').trim();
    if (!needle) return [];
    const nodes: GraphFindMatch[] = [];
    const edges: GraphFindMatch[] = [];
    const seen = new Set<string>();
    for (const el of elements) {
        const data = el?.data;
        if (!data || typeof data.id !== 'string' || seen.has(data.id)) continue;
        const field = matchElementData(data, needle);
        if (!field) continue;
        seen.add(data.id);
        const isEdge = data.source != null && data.target != null;
        (isEdge ? edges : nodes).push({ id: data.id, kind: isEdge ? 'edge' : 'node', field });
    }
    return [...nodes, ...edges];
}

/** Next index when cycling with Enter (Shift+Enter goes back). */
export function nextFindIndex(current: number, total: number, backwards = false): number {
    if (total <= 0) return -1;
    if (current < 0) return backwards ? total - 1 : 0;
    return backwards ? (current - 1 + total) % total : (current + 1) % total;
}
